import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { saveServizi } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelBody, PanelHead, Button, Spinner } from '../components/ui';

// ─── Icone disponibili per i servizi ─────────────────────────────────────────
// Raggruppate per categoria per facilitare la scelta del gestore
const ICONE_DISPONIBILI = [
  {
    gruppo: "Spiaggia",
    icone: [
      { name: "beach_access", label: "Ombrellone" },
      { name: "deck", label: "Sdraio" },
      { name: "bedroom_child", label: "Lettino" },
      { name: "holiday_village", label: "Gazebo" },
      { name: "chair", label: "Sedia" },
      { name: "king_bed", label: "Letto" },
      { name: "umbrella", label: "Parasole" },
      { name: "surfing", label: "Surf" },
      { name: "kayaking", label: "Kayak" },
      { name: "pool", label: "Piscina" },
      { name: "water", label: "Acqua" },
      { name: "waves", label: "Mare" },
    ],
  },
  {
    gruppo: "Strutture",
    icone: [
      { name: "local_bar", label: "Bar" },
      { name: "restaurant", label: "Ristorante" },
      { name: "coffee", label: "Caffetteria" },
      { name: "shower", label: "Docce" },
      { name: "wc", label: "WC" },
      { name: "storefront", label: "Chiosco" },
      { name: "spa", label: "Spa" },
      { name: "sports_tennis", label: "Tennis" },
      { name: "sports_volleyball", label: "Pallavolo" },
      { name: "fitness_center", label: "Palestra" },
      { name: "park", label: "Area verde" },
      { name: "local_parking", label: "Parcheggio" },
    ],
  },
  {
    gruppo: "Extra e servizi",
    icone: [
      { name: "wifi", label: "WiFi" },
      { name: "electric_bolt", label: "Elettricità" },
      { name: "pets", label: "Animali" },
      { name: "child_care", label: "Bambini" },
      { name: "accessible", label: "Disabili" },
      { name: "lunch_dining", label: "Pranzo" },
      { name: "ice_cream", label: "Gelateria" },
      { name: "local_pizza", label: "Pizzeria" },
      { name: "directions_bike", label: "Bici" },
      { name: "anchor", label: "Barca" },
      { name: "sailing", label: "Vela" },
      { name: "scuba_diving", label: "Immersioni" },
    ],
  },
];

const COLORI = [
  '#1f93e0', '#2A6E7F', '#3a8fa3', '#0f6e56', '#854F0B',
  '#7F77DD', '#D85A30', '#D4537E', '#639922', '#BA7517',
  '#c0392b', '#8e44ad', '#16a085', '#d35400', '#2980b9',
];

function MSIcon({ name, size = 20, color = "currentColor" }) {
  if (!name) return null;
  return (
    <span className="material-symbols-outlined" style={{
      fontSize: `${size}px`, color, lineHeight: 1, display: "block",
      fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
      userSelect: "none",
    }}>
      {name}
    </span>
  );
}

// ─── Selettore icone ──────────────────────────────────────────────────────────
function IconSelector({ value, onChange }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return ICONE_DISPONIBILI;
    const q = search.toLowerCase();
    return ICONE_DISPONIBILI.map(g => ({
      ...g,
      icone: g.icone.filter(i => i.label.toLowerCase().includes(q) || i.name.includes(q)),
    })).filter(g => g.icone.length > 0);
  }, [search]);

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)', padding: '12px',
    }}>
      {/* Selezione attuale */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '8px',
          background: 'var(--navy)', border: '1px solid var(--border-mid)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {value
            ? <MSIcon name={value} size={18} color="var(--ocean)" />
            : <span style={{ fontSize: '18px', color: 'var(--text-muted)' }}>?</span>
          }
        </div>
        <div>
          <div style={{ fontSize: '13px', color: 'var(--text-soft)' }}>
            {value ? ICONE_DISPONIBILI.flatMap(g => g.icone).find(i => i.name === value)?.label || value : 'Nessuna icona selezionata'}
          </div>
          {value && (
            <button onClick={() => onChange('')} style={{
              fontSize: '11px', color: 'var(--text-muted)', background: 'none',
              border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)',
            }}>
              Rimuovi icona
            </button>
          )}
        </div>
      </div>

      {/* Ricerca */}
      <input
        placeholder="Cerca icona..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: '100%', background: 'var(--navy)', border: '1px solid var(--border-mid)',
          borderRadius: 'var(--radius-md)', color: '#fff', padding: '7px 12px',
          fontSize: '13px', fontFamily: 'var(--font-sans)', marginBottom: '12px',
          boxSizing: 'border-box',
        }}
      />

      {/* Griglia icone */}
      <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
        {filtered.map(gruppo => (
          <div key={gruppo.gruppo} style={{ marginBottom: '12px' }}>
            <div style={{
              fontSize: '10px', color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.08em',
              marginBottom: '6px',
            }}>
              {gruppo.gruppo}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {gruppo.icone.map(icona => (
                <button
                  key={icona.name}
                  onClick={() => onChange(icona.name)}
                  title={icona.label}
                  style={{
                    width: '36px', height: '36px', borderRadius: '6px',
                    border: `1px solid ${value === icona.name ? 'var(--ocean)' : 'var(--border)'}`,
                    background: value === icona.name ? 'rgba(31,147,224,0.15)' : 'var(--navy)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.1s',
                  }}
                  onMouseEnter={e => { if (value !== icona.name) e.currentTarget.style.borderColor = 'var(--border-mid)'; }}
                  onMouseLeave={e => { if (value !== icona.name) e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <MSIcon name={icona.name} size={18} color={value === icona.name ? 'var(--ocean)' : 'var(--text-muted)'} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Form servizio ────────────────────────────────────────────────────────────
function ServizioForm({ servizio, onSave, onCancel }) {
  const isNew = !servizio.id;
  const [form, setForm] = useState({
    id: servizio.id || `s${Date.now()}`,
    label: servizio.label || '',
    tipo: servizio.tipo || 'ombrellone',
    colore: servizio.colore || '#1f93e0',
    prenotabile: servizio.prenotabile !== false,
    iconaId: servizio.iconaId || '',
    prezzo: servizio.prezzo || 0,
    haFila: servizio.haFila || false,
    capacitaMax: servizio.capacitaMax || '',
    policy: servizio.policy || '',
    inclusi: servizio.inclusi || [],
    extra: servizio.extra || [],
  });

  const setF = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const setFNum = (k) => (e) => setForm(p => ({ ...p, [k]: Number(e.target.value) || 0 }));
  const setFBool = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.checked }));

  const addIncluso = () => setForm(p => ({ ...p, inclusi: [...p.inclusi, { label: '', quantita: 1 }] }));
  const removeIncluso = (i) => setForm(p => ({ ...p, inclusi: p.inclusi.filter((_, idx) => idx !== i) }));
  const updateIncluso = (i, field, val) => setForm(p => ({
    ...p,
    inclusi: p.inclusi.map((inc, idx) => idx === i ? { ...inc, [field]: val } : inc),
  }));

  const addExtra = () => setForm(p => ({
    ...p,
    extra: [...p.extra, { id: `e${Date.now()}`, label: '', prezzo: 0, attivo: true, maxQuantita: 1 }],
  }));
  const removeExtra = (i) => setForm(p => ({ ...p, extra: p.extra.filter((_, idx) => idx !== i) }));
  const updateExtra = (i, field, val) => setForm(p => ({
    ...p,
    extra: p.extra.map((ex, idx) => idx === i ? { ...ex, [field]: val } : ex),
  }));

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      background: 'var(--navy-mid)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '16px',
    }}>
      <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '20px', fontFamily: 'var(--font-serif)' }}>
        {isNew ? 'Nuovo servizio' : `Modifica — ${servizio.label}`}
      </div>

      {/* Riga 1: nome, tipo, prezzo */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Nome servizio *</label>
          <input placeholder="es. Ombrellone Classic" value={form.label} onChange={setF('label')} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Tipo</label>
          <select value={form.tipo} onChange={setF('tipo')} style={inputStyle}>
            <option value="ombrellone">Ombrellone</option>
            <option value="area">Area (capienza)</option>
            <option value="struttura">Struttura</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Prezzo base €</label>
          <input type="number" min="0" step="0.5" value={form.prezzo} onChange={setFNum('prezzo')} style={inputStyle} />
        </div>
      </div>

      {/* Riga 2: colore, icona, opzioni */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>Colore sulla mappa</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {COLORI.map(c => (
              <div key={c} onClick={() => setForm(p => ({ ...p, colore: c }))} style={{
                width: '22px', height: '22px', borderRadius: '50%', background: c,
                cursor: 'pointer',
                border: form.colore === c ? '2px solid #fff' : '2px solid transparent',
                transition: 'border 0.1s',
              }} />
            ))}
          </div>
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Opzioni</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '12px', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.prenotabile} onChange={setFBool('prenotabile')} />
              Prenotabile dai clienti
            </label>
            <label style={{ fontSize: '12px', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.haFila} onChange={setFBool('haFila')} />
              Prezzo varia per fila
            </label>
          </div>
        </div>
      </div>

      {/* Capienza massima (solo per area) */}
      {form.tipo === 'area' && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Capienza massima</label>
          <input type="number" min="1" value={form.capacitaMax}
            onChange={e => setForm(p => ({ ...p, capacitaMax: Number(e.target.value) || '' }))}
            style={{ ...inputStyle, width: '120px' }} placeholder="es. 20" />
        </div>
      )}

      {/* Icona */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Icona sulla mappa
        </label>
        <IconSelector value={form.iconaId} onChange={v => setForm(p => ({ ...p, iconaId: v }))} />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Cosa è incluso
          </label>
          <button onClick={addIncluso} style={{
            fontSize: '11px', color: 'var(--ocean)', background: 'none',
            border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)',
          }}>+ Aggiungi</button>
        </div>
        {form.inclusi.map((inc, i) => (
          <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
            <input placeholder="es. Lettino" value={inc.label}
              onChange={e => updateIncluso(i, 'label', e.target.value)}
              style={{ ...inputStyle, flex: 1 }} />
            <input type="number" min="1" value={inc.quantita}
              onChange={e => updateIncluso(i, 'quantita', Number(e.target.value))}
              style={{ ...inputStyle, width: '70px' }} />
            <button onClick={() => removeIncluso(i)} style={{
              width: '28px', height: '28px', borderRadius: '50%',
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          </div>
        ))}
        {form.inclusi.length === 0 && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '8px 0' }}>
            Nessun elemento incluso — clicca + Aggiungi
          </div>
        )}
      </div>

      {/* Extra opzionali */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Extra opzionali
          </label>
          <button onClick={addExtra} style={{
            fontSize: '11px', color: 'var(--ocean)', background: 'none',
            border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)',
          }}>+ Aggiungi</button>
        </div>
        {form.extra.map((ex, i) => (
          <div key={i} style={{
            display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center',
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: '8px 10px',
          }}>
            <input placeholder="es. Lettino aggiuntivo" value={ex.label}
              onChange={e => updateExtra(i, 'label', e.target.value)}
              style={{ ...inputStyle, flex: 1, background: 'transparent', border: 'none', padding: '0' }} />
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--sand)' }}>€</span>
              <input type="number" min="0" step="0.5" value={ex.prezzo}
                onChange={e => updateExtra(i, 'prezzo', Number(e.target.value))}
                style={{ ...inputStyle, width: '80px', paddingLeft: '20px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Max</span>
              <input type="number" min="1" value={ex.maxQuantita ?? 1}
                onChange={e => updateExtra(i, 'maxQuantita', Number(e.target.value) || 1)}
                style={{ ...inputStyle, width: '54px', padding: '4px 6px', fontSize: '12px' }} />
            </div>
            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', flexShrink: 0 }}>
              <input type="checkbox" checked={ex.attivo}
                onChange={e => updateExtra(i, 'attivo', e.target.checked)} />
              Attivo
            </label>
            <button onClick={() => removeExtra(i)} style={{
              width: '24px', height: '24px', borderRadius: '50%',
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px',
            }}>×</button>
          </div>
        ))}
        {form.extra.length === 0 && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '8px 0' }}>
            Nessun extra configurato
          </div>
        )}
      </div>

      {/* Policy */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Policy / Note per il cliente
        </label>
        <textarea
          rows={3}
          placeholder="es. Cambio turno alle 13:00. No animali. I lettini inclusi non sono spostabili."
          value={form.policy}
          onChange={setF('policy')}
          style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <Button variant="primary" onClick={() => onSave(form)} disabled={!form.label}>
          {isNew ? 'Crea servizio' : 'Salva modifiche'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </div>
  );
}

// ─── Card servizio ────────────────────────────────────────────────────────────
function ServizioCard({ servizio, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Panel style={{ marginBottom: '10px' }}>
      <div onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '14px 20px', cursor: 'pointer',
      }}>
        {/* Icona */}
        <div style={{
          width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
          background: `${servizio.colore}20`, border: `1px solid ${servizio.colore}50`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {servizio.iconaId
            ? <MSIcon name={servizio.iconaId} size={18} color={servizio.colore} />
            : <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: servizio.colore }} />
          }
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>{servizio.label}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <span>€ {servizio.prezzo}</span>
            {servizio.tipo === 'area' && servizio.capacitaMax && <span>max {servizio.capacitaMax} posti</span>}
            {servizio.inclusi?.length > 0 && <span>include: {servizio.inclusi.map(i => `${i.quantita} ${i.label}`).join(', ')}</span>}
            {servizio.extra?.filter(e => e.attivo).length > 0 && (
              <span>{servizio.extra.filter(e => e.attivo).length} extra disponibili</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); onEdit(); }}>
            Modifica
          </Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); onDelete(); }}>
            Elimina
          </Button>
        </div>

        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: 'var(--text-muted)', flexShrink: 0 }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '14px' }}>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                Inclusi
              </div>
              {servizio.inclusi?.length > 0
                ? servizio.inclusi.map((inc, i) => (
                  <div key={i} style={{ fontSize: '13px', color: 'var(--text-soft)', marginBottom: '3px' }}>
                    {inc.quantita}× {inc.label}
                  </div>
                ))
                : <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Nessuno</div>
              }
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                Extra disponibili
              </div>
              {servizio.extra?.filter(e => e.attivo).length > 0
                ? servizio.extra.filter(e => e.attivo).map((ex, i) => (
                  <div key={i} style={{ fontSize: '13px', color: 'var(--text-soft)', marginBottom: '3px' }}>
                    {ex.label} — € {ex.prezzo}{ex.maxQuantita > 1 ? ` · max ${ex.maxQuantita}` : ''}
                  </div>
                ))
                : <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Nessuno</div>
              }
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                Policy
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-soft)', lineHeight: 1.5 }}>
                {servizio.policy || '—'}
              </div>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function Servizi() {
  const { beach, admin, refreshBeach } = useAuth();
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const servizi = beach?.servizi || [];
  console.log("🚀 ~ Servizi ~ beach?.servizi:", beach?.servizi)

  const handleSave = async (servizioAggiornato) => {
    setSaving(true);
    try {
      const nuovi = editing?.id && servizi.find(s => s.id === editing.id)
        ? servizi.map(s => s.id === servizioAggiornato.id ? servizioAggiornato : s)
        : [...servizi, servizioAggiornato];
      await saveServizi(admin.beachCode, nuovi);
      await refreshBeach();
      setEditing(null);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo servizio?')) return;
    await saveServizi(admin.beachCode, servizi.filter(s => s.id !== id));
    await refreshBeach();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        title="Servizi"
        subtitle="Configura i servizi del tuo lido"
        actions={
          <Button variant="primary" onClick={() => setEditing({})}>
            + Nuovo servizio
          </Button>
        }
      />

      <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
        {editing !== null && (
          <ServizioForm
            servizio={editing}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}

        {servizi.length === 0 && editing === null ? (
          <Panel>
            <PanelBody>
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>🏖️</div>
                <div style={{ fontSize: '13px', marginBottom: '16px' }}>
                  Nessun servizio configurato.<br />
                  Aggiungi ombrelloni, lettini, gazebo e tutto ciò che offre il tuo lido.
                </div>
                <Button variant="primary" onClick={() => setEditing({})}>
                  + Crea primo servizio
                </Button>
              </div>
            </PanelBody>
          </Panel>
        ) : (
          <div>
            {servizi.map(s => (
              <ServizioCard
                key={s.id}
                servizio={s}
                onEdit={() => setEditing(s)}
                onDelete={() => handleDelete(s.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}